import type {
  HabitPayloadDto,
  HabitTypeDto,
  HolidayDto,
  HolidayPayloadDto,
  SuggestionDto,
  SuggestionStatusDto,
} from './proactive.dto';

export type SuggestionStatus = SuggestionStatusDto;

export interface HolidayInfo {
  key: string;
  name: string;
  /** Local midnight of the day it is celebrated. */
  date: Date;
  /** Local midnight of the federal day off — where the calendar badge sits. */
  observedDate: Date;
  federal: boolean;
}

export interface EventPrefill {
  title: string;
  description: string;
  start: Date;
  end: Date;
  locations: string[];
  eventType: string | null;
}

// Older suggestions carry only the day: a daytime window on it.
const FALLBACK_START_HOUR = 10;
const FALLBACK_END_HOUR = 18;

const atHour = (day: Date, hour: number) =>
  new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour);

const parseInstant = (value: string | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** What every suggestion card shows, whatever the agent based it on. */
interface SuggestionBase {
  id: string;
  vesselId: string;
  title: string;
  description: string;
  tags: string[];
  reason: string;
  status: SuggestionStatus;
  topic: string | null;
  seenAt: Date | null;
  createdAt: Date;
  /** After this the idea is pointless (the end of the holiday, the routine's day long gone); reminders must come before it. */
  expiresAt: Date | null;
  eventPrefill: EventPrefill;
}

export interface HolidaySuggestion extends SuggestionBase {
  kind: 'holiday';
  holiday: HolidayInfo;
}

export type HabitType = HabitTypeDto;

export interface HabitInfo {
  type: HabitType;
  /** The place, the company or the kind of outing. */
  label: string;
  intervalDays: number;
  occurrences: number;
  lastDate: Date;
  expectedDate: Date;
}

/** A routine of the vessel's coming round again (same place, same people, same kind of outing). */
export interface HabitSuggestion extends SuggestionBase {
  kind: 'habit';
  habit: HabitInfo;
  /** Local midnight of the day the card is about — highlighted in the calendar. */
  date: Date;
  /** Who usually comes. Offered, never pre-selected. */
  suggestedGuests: { personId: string; name: string }[];
}

export type ProactiveSuggestion = HolidaySuggestion | HabitSuggestion;

/** "2026-10-31" → local midnight. `new Date('2026-10-31')` would be UTC midnight,
 *  which lands on the 30th west of Greenwich. */
export const parseLocalDate = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};

const isHolidayPayload = (
  payload: Record<string, unknown>,
): payload is HolidayPayloadDto & Record<string, unknown> => {
  const holiday = payload.holiday as Partial<HolidayPayloadDto['holiday']> | undefined;
  return typeof holiday?.name === 'string' && typeof holiday.observed_date === 'string';
};

const isHabitPayload = (
  payload: Record<string, unknown>,
): payload is HabitPayloadDto & Record<string, unknown> => {
  const habit = payload.habit as Partial<HabitPayloadDto['habit']> | undefined;
  return (
    typeof habit?.label === 'string' &&
    typeof habit.expected_date === 'string' &&
    typeof payload.date === 'string' &&
    typeof payload.event_prefill === 'object' &&
    payload.event_prefill !== null
  );
};

const mapBase = (dto: SuggestionDto) => ({
  id: dto.id,
  vesselId: dto.vessel_id,
  title: dto.title,
  description: dto.description,
  tags: dto.tags,
  reason: dto.reason,
  status: dto.status,
  topic: dto.topic,
  seenAt: dto.seen_at ? new Date(dto.seen_at) : null,
  createdAt: new Date(dto.created_at),
  expiresAt: dto.expires_at ? new Date(dto.expires_at) : null,
});

export const mapHolidaySuggestion = (dto: SuggestionDto): HolidaySuggestion | null => {
  if (dto.kind !== 'holiday' || !isHolidayPayload(dto.payload)) return null;
  const { holiday, event_prefill } = dto.payload;
  return {
    ...mapBase(dto),
    kind: 'holiday',
    holiday: {
      key: holiday.key,
      name: holiday.name,
      date: parseLocalDate(holiday.date),
      observedDate: parseLocalDate(holiday.observed_date),
      federal: holiday.federal,
    },
    eventPrefill: mapEventPrefill(dto, event_prefill, holiday.date),
  };
};

const mapEventPrefill = (
  dto: SuggestionDto,
  prefill: HolidayPayloadDto['event_prefill'],
  holidayDate: string,
): EventPrefill => {
  const day = parseLocalDate(prefill.date || holidayDate);
  const start = parseInstant(prefill.start);
  const end = parseInstant(prefill.end);
  const hasWindow = !!start && !!end && end > start;
  return {
    title: prefill.title || dto.title,
    description: prefill.description || dto.description,
    start: hasWindow ? start : atHour(day, FALLBACK_START_HOUR),
    end: hasWindow ? end : atHour(day, FALLBACK_END_HOUR),
    locations: (prefill.locations ?? []).filter((place) => place.trim().length > 0),
    eventType: prefill.event_type ?? null,
  };
};

export const mapHabitSuggestion = (dto: SuggestionDto): HabitSuggestion | null => {
  if (dto.kind !== 'habit' || !isHabitPayload(dto.payload)) return null;
  const { habit, date, suggested_guests, event_prefill } = dto.payload;
  return {
    ...mapBase(dto),
    kind: 'habit',
    habit: {
      type: habit.type,
      label: habit.label,
      intervalDays: habit.interval_days,
      occurrences: habit.occurrences,
      lastDate: parseLocalDate(habit.last_date),
      expectedDate: parseLocalDate(habit.expected_date),
    },
    date: parseLocalDate(date),
    suggestedGuests: (suggested_guests ?? [])
      .filter((guest) => guest.person_id && guest.name?.trim())
      .map((guest) => ({ personId: guest.person_id, name: guest.name.trim() })),
    eventPrefill: mapEventPrefill(dto, event_prefill, date),
  };
};

/**
 * Holiday and habit suggestions; anything else the agent may send later (other
 * kinds, a payload we don't understand) is dropped here rather than rendered
 * half-empty.
 */
export const mapSuggestion = (dto: SuggestionDto): ProactiveSuggestion | null =>
  mapHolidaySuggestion(dto) ?? mapHabitSuggestion(dto);

export const mapSuggestions = (items: SuggestionDto[]): ProactiveSuggestion[] =>
  items.map(mapSuggestion).filter((s): s is ProactiveSuggestion => s !== null);

/** The day a suggestion's calendar badge sits on. */
export const suggestionDay = (suggestion: ProactiveSuggestion): Date =>
  suggestion.kind === 'holiday' ? suggestion.holiday.observedDate : suggestion.date;

/** "day_cruise" → "Day cruise" */
export const tagLabel = (tag: string): string => {
  const words = tag.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** A holiday as the calendar shows it. */
export interface CalendarHolidayInfo {
  key: string;
  name: string;
  date: Date;
  observedDate: Date;
  federal: boolean;
  ideas: boolean;
}

export const mapHoliday = (dto: HolidayDto): CalendarHolidayInfo => ({
  key: dto.key,
  name: dto.name,
  date: parseLocalDate(dto.date),
  observedDate: parseLocalDate(dto.observed_date),
  federal: dto.federal,
  ideas: dto.ideas,
});
